import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Equidad",
  "lead": "Ha habido progresos a nivel mundial con respecto a la paridad de género en la educación, pero siguen existiendo grandes brechas entre las personas de zonas rurales y urbanas, y entre los ricos y los pobres."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, PageLink, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky, Tooltip} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!PageLink) _missingMdxReference("PageLink", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!Tooltip) _missingMdxReference("Tooltip", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_EQUITY"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "El deseo de no dejar a nadie atrás atañe a todos los Objetivos de Desarrollo Sostenible (ODS) y se menciona explícitamente en la quinta meta del objetivo de educación, el ODS 4. Se trata de asegurar que los responsables políticos den prioridad a ayudar primero a los más rezagados."
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Para poder verificar que se estén cerrando las brechas educativas, necesitamos datos desglosados ", _jsx(_components.strong, {
        children: "por ingresos, género, edad, raza, origen étnico, situación migratoria, discapacidad, ubicación geográfica y otras características"
      }), " pertinentes para los contextos nacionales, que provienen de encuestas de hogares."]
    }), "\n", _jsxs(_components.p, {
      children: ["Desde 2010, la ", _jsx(_components.a, {
        href: "https://www.education-inequalities.org/",
        children: "Base de Datos sobre la Desigualdad Mundial en la Educación"
      }), " Base de Datos sobre la Desigualdad Mundial en la Educación utiliza encuestas de hogares para mostrar cuán amplias son las brechas educativas en los distintos países. En este artículo se resumen algunos de los hechos y tendencias clave de la desigualdad en la educación, mientras que ciertos aspectos adicionales se abordan en el artículo sobre ", _jsx(PageLink, {
        name: "financia",
        children: "financiación"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "género",
      children: "Género"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.5.1",
      children: [_jsxs(_components.p, {
        children: ["En el gráfico a continuación se abordan las disparidades en materia de educación entre diversos grupos, como las niñas y los niños, o los más pobres y los más ricos, para cuyo cálculo se emplea un ", _jsx(Tooltip, {
          title: "índice de paridad",
          content: "Si un índice de paridad está cerca de 0, el grupo desfavorecido está muy rezagado; si está cerca de 1, hay paridad; y si está cerca de 2, el grupo favorecido es el que tiene más retraso. Por ejemplo, un índice de paridad de 0,5 podría significar que hay un 30% de niñas y un 60% de niños matriculados. Un índice de paridad de 1,5 supondría que hay un 30% de niños y un 60% de niñas matriculados.",
          children: "parity index"
        }), "."]
      }), _jsxs(_components.p, {
        children: ["Aunque se celebre con poca frecuencia, la ", _jsx(Tooltip, {
          title: "paridad de género",
          content: "Cuando hay igual número de niños que de niñas",
          children: "gender parity"
        }), " ha mejorado de forma continua en la última generación. Las niñas y las mujeres jóvenes de muchas partes del mundo, por lo general las más ricas, superan en número a los niños y los hombres jóvenes en las escuelas y universidades."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "GPITREND"
          })
        }), _jsx(ScrollySection, {
          id: "income,0",
          children: _jsxs(_components.p, {
            children: ["Toda línea que pase por debajo de la ", _jsx(HighlightByIndex, {
              index: "-1",
              children: "zona sombreada en gris "
            }), " muestra que la cantidad de niños u hombres jóvenes matriculados es superior a la de las niñas."]
          })
        }), _jsx(ScrollySection, {
          id: "geo,1",
          children: _jsxs(_components.p, {
            children: ["En ", _jsx(HighlightByIndex, {
              index: "0",
              children: "África Subsahariana"
            }), ", en la educación terciaria todavía hay menos de cuatro mujeres jóvenes por cada cinco hombres jóvenes."]
          })
        }), _jsx(ScrollySection, {
          id: "geo,2",
          children: _jsxs(_components.p, {
            children: [_jsx(HighlightByIndex, {
              index: "2",
              children: "Asia central y meridional"
            }), ", donde las niñas se habían quedado muy atrás en 1990, ya han alcanzado la paridad de género en la educación primaria a terciaria, sobre todo gracias a los espectaculares progresos realizados en la India. Esto ha llevado a la región a superar a ", _jsx(HighlightByIndex, {
              index: "0",
              children: "África Subsahariana "
            }), " en términos del progreso alcanzado para lograr la paridad de género."]
          })
        })]
      }), _jsx(_components.p, {
        children: "En el plano mundial, son grandes los avances conseguidos desde el año 2000. Sin embargo, si se desciende al nivel nacional se descubren brechas persistentes; así pues, solo tres de cada cuatro países han logrado la paridad de género con respecto a la matriculación en la educación primaria, alrededor de uno de cada dos en el primer ciclo de la educación secundaria, y menos de uno de cada tres en el segundo ciclo de la educación secundaria."
      }), _jsxs(_components.p, {
        children: ["La proporción de países con una ", _jsx(HighlightByIndex, {
          index: "2",
          children: "gran disparidad en detrimento de las niñas "
        }), " se ha reducido al menos a la mitad desde 2000 en la educación primaria a terciaria, pero sigue siendo del 2% en la educación primaria, el 6% en el primer ciclo de la educación secundaria, y el 9% en el segundo ciclo de la educación secundaria."]
      }), _jsxs(_components.p, {
        children: ["No debemos olvidar a los chicos cuando hablamos de igualdad de género. Si bien es menos común que los países tengan una ", _jsx(HighlightByIndex, {
          index: "0",
          children: "disparidad en detrimento de los chicos"
        }), " en la educación primaria, las desigualdades son mucho más comunes en el segundo ciclo de secundaria y los niveles superiores."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PARITYCOUNT"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["En este gráfico, cuanto mayor y más oscura es la parte azul de la barra, son más los países con ", _jsx(HighlightByIndex, {
              index: "0",
              children: "más niños que niñas sin escolarizar"
            }), ". Cuanto mayor y más oscura es la parte verde de la barra, son más los países con ", _jsx(HighlightByIndex, {
              index: "2",
              children: "más niñas que niños sin escolarizar"
            }), "."]
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["En la educación terciaria, por ejemplo, tres cuartos de los países presentan ", _jsx(HighlightByIndex, {
              index: "0",
              children: "profundas disparidades en detrimento de los chicos"
            }), "."]
          })
        })]
      }), _jsx(_components.h2, {
        id: "riqueza",
        children: "Riqueza"
      }), _jsx(_components.p, {
        children: "En general, las disparidades en materia de educación entre niñas y niños no son tan grandes como entre las zonas rurales y urbanas, y entre los ricos y pobres, según revelan las encuestas de hogares."
      }), _jsxs(_components.p, {
        children: ["En Madagascar, por ejemplo, mientras que el 85% de los ", _jsx(HighlightByIndex, {
          index: "0",
          children: "más ricos "
        }), " termina la educación primaria, entre los ", _jsx(HighlightByIndex, {
          index: "1",
          children: "más pobres "
        }), " el porcentaje es solo del 13%. En Nigeria, el 94% de los más ricos recibe educación del primer ciclo de secundaria, pero este es el caso únicamente para el 27% de los más pobres."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PSECWGAP"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["Los puntos rojos muestran qué porcentaje de niños o jóvenes ", _jsx(HighlightByIndex, {
              index: "1",
              children: "más pobres "
            }), " van a la escuela, en comparación con los ", _jsx(HighlightByIndex, {
              index: "0",
              children: "más ricos"
            }), ", que se muestran en azul. Puede cambiar el nivel de educación de primaria a primer ciclo de secundaria y niveles superiores en la parte superior del gráfico."]
          })
        }), _jsx(ScrollySection, {
          id: "PHL",
          children: _jsxs(_components.p, {
            children: ["En la educación terciaria, las brechas son mayores en los países de ingresos medianos. En ", _jsx(_components.strong, {
              children: "Filipinas"
            }), ", por ejemplo, el 61% de los más ricos, pero menos del 20% de los más pobres, cursó alguna modalidad de estudios postsecundarios."]
          })
        }), _jsx(ScrollySection, {
          id: "NPL",
          children: _jsxs(_components.p, {
            children: ["En ", _jsx(_components.strong, {
              children: "Nepal"
            }), ", los porcentajes respectivos eran del 48% y el 5%."]
          })
        })]
      }), _jsx(_components.p, {
        children: "Es aún más revelador observar la forma en que cambian las brechas a medida que los niños prosiguen sus estudios."
      }), _jsxs(_components.p, {
        children: ["El examen a continuación de las brechas de ", _jsx(_components.strong, {
          children: "gender"
        }), " gaps en ", _jsx(HighlightByIndex, {
          index: "0",
          children: "Burkina Faso"
        }), " demuestra hasta qué punto las niñas abandonan la escuela cuanto mayor es el nivel de educación."]
      }), _jsxs(_components.p, {
        children: ["Por otra parte, seleccionar la ", _jsx(_components.strong, {
          children: "riqueza"
        }), " pone de manifiesto cómo se acumulan las brechas a lo largo de la trayectoria educativa en detrimento de los más pobres, ya que muy pocos de este grupo prosiguen después del segundo ciclo de secundaria."]
      }), _jsx(Figure, {
        id: "WLGPI"
      })]
    }), "\n", _jsx(_components.p, {
      children: "Rara vez un niño pertenece a una sola categoría, lo que significa que es crucial considerar también la forma en que las características individuales, como el sexo, interactúan con otras, como la riqueza, tal como se hace en la siguiente figura."
    }), "\n", _jsxs(_components.p, {
      children: ["En África Subsahariana, las brechas entre los ", _jsx(HighlightByIndex, {
        index: "1",
        children: "más pobres "
      }), " y los ", _jsx(HighlightByIndex, {
        index: "0",
        children: "más ricos "
      }), " se ensanchan a lo largo de la trayectoria educativa, ya que por cada 100 de sus pares masculinos, menos de 35 de las niñas más pobres, frente a casi 100 de las más ricas, reciben educación terciaria."]
    }), "\n", _jsx(_components.p, {
      children: "En Asia oriental y sudoriental, en cambio, los puntos suben y se alejan de la línea gris porque las brechas entre los géneros van en detrimento de los niños más pobres. A nivel de educación terciaria, la asistencia de las niñas más ricas es aproximadamente un 30 % mayor que la de sus compañeros varones, mientras que la de las niñas más pobres es un 40 % mayor."
    }), "\n", _jsx(Figure, {
      id: "GPIBYWEALTH"
    }), "\n", _jsx(_components.h2, {
      id: "conclusión",
      children: "Conclusión"
    }), "\n", _jsx(_components.p, {
      children: "Existen niveles inaceptables de desigualdad en la educación entre países y entre grupos dentro de un mismo país. Es indispensable contar con datos desglosados de alta calidad para formular estrategias apropiadas que permitan reducir estas desigualdades y, en última instancia, no dejar a nadie atrás en nuestro empeño por alcanzar el ODS 4."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.education-inequalities.org/",
            children: "Worldwide Inequalities Database on Education (WIDE)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/publication/2025-gender-report",
            children: "Women lead for learning – GEM Gender Report 2025"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/2024genderreport",
            children: "Technology on her terms – GEM Gender Report 2024"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.savethechildren.org.uk/content/dam/gb/reports/policy/still-left-behind-low-res.pdf",
            children: "Still left behind – Save the Children"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
